// ─── Excuses ──────────────────────────────────────────────────────────────────

export type ExcuseStyle = 'soft' | 'personal' | 'chaos';

export interface GeneratedExcuse {
  type: string;
  text: string;
}

const EXCUSE_TYPE_LABEL: Record<ExcuseStyle, string> = {
  soft: 'THE SOFT BOUNDARY',
  personal: 'THE PERSONAL TRUTH',
  chaos: 'THE CHAOS CARD',
};
const EXCUSE_ORDER: ExcuseStyle[] = ['soft', 'personal', 'chaos'];

export async function generateExcuses(
  category: string,
  situation: string
): Promise<GeneratedExcuse[]> {
  const res = await fetch('/api/generate', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ category, situation }),
  });
  const data = await parse<{ excuses: Record<ExcuseStyle, string> }>(res);
  return EXCUSE_ORDER.map((key) => ({
    type: EXCUSE_TYPE_LABEL[key],
    text: data.excuses[key],
  }));
}

// ─── Replies (ImNotHavingIt) ──────────────────────────────────────────────────

export type ReplyStyle = 'final' | 'burn' | 'receipts' | 'nuke';

export interface GeneratedReply {
  type: string;
  text: string;
  style: ReplyStyle;
}

const REPLY_TYPE_LABEL: Record<ReplyStyle, string> = {
  final: 'THE FINAL WORD',
  burn: 'THE BURN',
  receipts: 'THE RECEIPTS',
  nuke: 'THE NUKE',
};
const REPLY_ORDER: ReplyStyle[] = ['final', 'burn', 'receipts', 'nuke'];

export async function generateReplies(
  platform: string,
  comment: string
): Promise<GeneratedReply[]> {
  const res = await fetch('/api/reply', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ platform, comment }),
  });
  const data = await parse<{ replies: Record<ReplyStyle, string> }>(res);
  return REPLY_ORDER.map((key) => ({
    type: REPLY_TYPE_LABEL[key],
    text: data.replies[key],
    style: key,
  }));
}

// ─── Refine (shared) ──────────────────────────────────────────────────────────

export type RefineStyle =
  | 'shorter'
  | 'warmer'
  | 'dramatic'
  | 'sharper'
  | 'calmer';

export async function refineText(text: string, style: RefineStyle): Promise<string> {
  const res = await fetch('/api/refine', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text, style }),
  });
  const data = await parse<{ text: string }>(res);
  return data.text;
}

// ─── Hall of Fame (excuses) ───────────────────────────────────────────────────

export interface HofCard {
  id: string;
  type: string;
  text: string;
  category: string;
  votes: number;
  created_at: number;
}

export interface HofResponse {
  featured: HofCard | null;
  cards: HofCard[];
}

export async function fetchHof(filter: {
  time?: 'all' | 'week' | 'today';
  category?: string;
}): Promise<HofResponse> {
  const params = new URLSearchParams();
  if (filter.time) params.set('time', filter.time);
  if (filter.category && filter.category !== 'all') params.set('category', filter.category);
  const res = await fetch(`/api/hof?${params.toString()}`);
  return parse<HofResponse>(res);
}

export async function submitExcuse(input: {
  type: string;
  text: string;
  category: string;
}): Promise<{ id: string; status: 'approved' | 'pending' | 'rejected'; reason?: string }> {
  const res = await fetch('/api/submit', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parse(res);
}

export async function voteExcuse(id: string): Promise<{ votes: number; voted: boolean }> {
  const res = await fetch('/api/vote', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ id }),
    credentials: 'same-origin',
  });
  return parse(res);
}

// ─── Stats ────────────────────────────────────────────────────────────────────

export interface Stats {
  obligations_dodged: number;
  excuses_submitted: number;
  replies_generated?: number;
}

export async function fetchStats(): Promise<Stats> {
  const res = await fetch('/api/stats');
  return parse<Stats>(res);
}

// ─── Internals ────────────────────────────────────────────────────────────────

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}
